<script>
export default {
  name: 'ErrorDescriptionCard',
  props: {
    code: { type: String, default: '' },
    header: { type: String, default: '' },
    subHeader: { type: String, default: '' },
    body: { type: String, default: '' },
    footer: { type: String, default: '' },
    type: {
      type: String,
      default: 'info',
      validator: (v) => ['success', 'warn', 'error', 'info'].includes(v),
    },
    buttonText: { type: String, default: '' },
    closable: { type: Boolean, default: false },
    compact: { type: Boolean, default: false },
    selected: { type: Boolean, default: false },
    selectable: { type: Boolean, default: false },
    createdAt: {
      type: String,
      default: null,
    },
    createdBy: {
      type: String,
      default: null,
    },
    updatedAt: {
      type: String,
      default: null,
    },
    updatedBy: {
      type: String,
      default: null,
    },
  },
  computed: {
    theme() {
      const themes = {
        success: {
          headerBg: '#f0fdf4',
          dividerColor: '#16a34a', // Sesuai warna iconBorder
          iconBg: '#dcfce7',
          iconBorder: '#16a34a',
          iconColor: '#16a34a',
          iconClass: 'mdi-check',
          badgeBg: '#dcfce7',
          badgeColor: '#166534',
        },
        warn: {
          headerBg: '#fffbeb',
          dividerColor: '#d97706', // Sesuai warna iconBorder
          iconBg: '#fef3c7',
          iconBorder: '#d97706',
          iconColor: '#d97706',
          iconClass: 'mdi-alert-outline',
          badgeBg: '#fef3c7',
          badgeColor: '#92400e',
        },
        error: {
          headerBg: '#fef2f2',
          dividerColor: '#dc2626', // Sesuai warna iconBorder
          iconBg: '#fee2e2',
          iconBorder: '#dc2626',
          iconColor: '#dc2626',
          iconClass: 'mdi-alert-outline',
          badgeBg: '#fee2e2',
          badgeColor: '#991b1b',
        },
        info: {
          headerBg: '#f0f9ff',
          dividerColor: '#2563eb', // Sesuai warna iconBorder
          iconBg: '#e0f2fe',
          iconBorder: '#2563eb',
          iconColor: '#2563eb',
          iconClass: 'mdi-information-outline',
          badgeBg: '#e0f2fe',
          badgeColor: '#0369a1',
        },
      }
      return themes[this.type] || themes.info
    },
    resolvedButtonText() {
      if (this.buttonText) return this.buttonText
      const map = { success: 'Selesai', warn: 'Lanjutkan', error: 'Mengerti', info: 'OK' }
      return map[this.type] || 'OK'
    },
    subHeaderLabel() {
      const labels = {
        success: 'Apa selanjutnya?',
        warn: 'Apa yang perlu diperiksa?',
        error: 'Kenapa dikunci?',
        info: 'Informasi',
      }
      return labels[this.type] || 'Informasi'
    },
  },
  methods: {
    onAction() {
      this.$emit('action')
    },
    onClose() {
      this.$emit('close')
    },
    onEdit() {
      this.$emit('edit')
    },
    onSelect() {
      this.$emit('select')
    },
  },
}
</script>

<template>
  <!-- COMPACT MODE (Grid Card di List) -->
  <div
    v-if="compact"
    class="edc-card edc-card--compact"
    :class="{ 'edc-card--selected': selected }"
  >
    <div v-if="selectable" class="edc-card__checkbox">
      <b-form-checkbox :checked="selected" @change="onSelect" size="lg" />
    </div>

    <!-- Top Tinted Section -->
    <div
      class="edc-card__top"
      :style="{ backgroundColor: theme.headerBg, borderBottomColor: theme.dividerColor }"
    >
      <!-- Code Badge Row -->
      <div v-if="code" class="edc-card__code-row">
        <span class="edc-code-label">#{{ code }}</span>
      </div>

      <div class="edc-card__badge-row">
        <span
          class="edc-badge"
          :style="{ backgroundColor: theme.badgeBg, color: theme.badgeColor }"
        >
          <i class="mdi" :class="theme.iconClass" style="margin-right: 4px"></i>
          {{ type ? type.toUpperCase() : 'UNKNOWN' }}
        </span>
      </div>
      <div class="edc-card__icon-row">
        <div
          class="edc-icon edc-icon--sm"
          :style="{
            backgroundColor: theme.iconBg,
            borderColor: theme.iconBorder,
            borderWidth: '2px',
          }"
        >
          <i class="mdi" :class="theme.iconClass" :style="{ color: theme.iconColor }"></i>
        </div>
      </div>
      <h5 class="edc-card__title">{{ header || 'No Header' }}</h5>
      <p v-if="subHeader" class="edc-card__sub-header">{{ subHeader }}</p>
    </div>

    <!-- Bottom White Section -->
    <div class="edc-card__bottom">
      <div class="edc-card__body-preview">
        <div
          class="edc-body-content"
          v-html="body || '<i class=\'edc-no-content\'>No content</i>'"
        ></div>
      </div>

      <div class="edc-card__action-wrapper">
        <b-button class="w-100" variant="primary">
          {{ resolvedButtonText }}
        </b-button>
      </div>

      <div v-if="footer" class="edc-card__footer-text text-center mt-2">{{ footer }}</div>

      <div class="edc-card__actions mt-3 pt-2 border-top d-flex justify-content-end">
        <b-button variant="outline-primary" size="sm" class="rounded-pill px-3" @click="onEdit">
          <i class="mdi mdi-pencil-outline me-1"></i> Edit Record
        </b-button>
      </div>

      <!-- Tambahkan meta info di sini, hanya jika compact -->
      <div v-if="compact" class="edc-card__meta mt-2 pt-2 border-top">
        <div v-if="createdBy" class="edc-meta-item">
          <i class="mdi mdi-account-plus me-1"></i>
          <span
            >Created: {{ createdBy }}
            <span v-if="createdAt">({{ new Date(createdAt).toLocaleString() }})</span>
          </span>
        </div>
        <div v-if="updatedBy" class="edc-meta-item">
          <i class="mdi mdi-account-edit me-1"></i>
          <span
            >Updated: {{ updatedBy }}
            <span v-if="updatedAt">({{ new Date(updatedAt).toLocaleString() }})</span>
          </span>
        </div>
      </div>
    </div>
  </div>

  <!-- FULL MODE (Modal / Popup Preview) -->
  <div v-else class="edc-card edc-card--full">
    <button v-if="closable" class="edc-close-btn" @click="onClose" aria-label="Close">
      <i class="mdi mdi-close"></i>
    </button>

    <!-- Top Tinted Section -->
    <div
      class="edc-card__top edc-card__top--full"
      :style="{ backgroundColor: theme.headerBg, borderBottomColor: theme.dividerColor }"
    >
      <div class="edc-card__icon-row">
        <div
          class="edc-icon edc-icon--lg"
          :style="{
            backgroundColor: theme.iconBg,
            borderColor: theme.iconBorder,
            borderWidth: '2px',
          }"
        >
          <i class="mdi" :class="theme.iconClass" :style="{ color: theme.iconColor }"></i>
        </div>
      </div>
      <h4 class="edc-card__header-full">{{ header || 'Terjadi Kesalahan' }}</h4>
      <p class="edc-card__sub-header-full">
        {{ subHeader || subHeaderLabel }}
      </p>
    </div>

    <!-- Bottom White Section -->
    <div class="edc-card__bottom edc-card__bottom--full">
      <div v-if="body" class="edc-body-content edc-body-content--full" v-html="body"></div>

      <div class="edc-card__action-wrapper mt-4">
        <b-button class="w-100" variant="primary" @click="onAction">
          {{ resolvedButtonText }}
        </b-button>
      </div>

      <p v-if="footer" class="edc-card__footer text-center mt-3 mb-0">{{ footer }}</p>
    </div>
  </div>
</template>

<style scoped>
/* ===== BASE STYLES ===== */
:root {
  --edc-radius: 12px;
  --edc-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  --edc-transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  --edc-font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  --edc-color-text: #1a2332;
  --edc-color-text-muted: #6b7280;
  --edc-color-border: #cbd5e1;
  --edc-color-primary: #0d6efd;
}

.edc-card {
  font-family: var(--edc-font-family);
  background: #ffffff;
  border-radius: 12px;
  position: relative;
  box-shadow: var(--edc-shadow);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
  border: 1.5px solid #cbd5e1;
  transition: var(--edc-transition);
}

.edc-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-color: #bac9d9;
}

.edc-card--selected {
  border-color: var(--edc-color-primary);
  box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.1), 0 1px 3px rgba(0, 0, 0, 0.1);
}

.edc-card--compact {
  border: 1.5px solid #cbd5e1;
}

.edc-card--full {
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  border-radius: 12px;
  border: 1.5px solid #cbd5e1;
}

/* ===== TOP TINTED SECTION ===== */
.edc-card__top {
  padding: 16px 24px 20px 24px;
  border-bottom: 1.5px solid; /* Color overridden by inline style */
  text-align: center;
  position: relative;
}

.edc-card--compact .edc-card__top {
  padding-top: 12px;
}

.edc-card__top--full {
  padding: 40px 32px 32px 32px;
  border-bottom: 1.5px solid var(--edc-color-border); /* Color overridden by inline style */
}

.edc-close-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: #6b7280;
  font-size: 1.5rem;
  cursor: pointer;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: var(--edc-transition);
  z-index: 10;
}

.edc-close-btn:hover {
  background: rgba(0, 0, 0, 0.05);
  color: var(--edc-color-text);
}

.edc-card__checkbox {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 11;
}

.edc-card__code-row {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 8px;
}

.edc-code-label {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-family: 'Monaco', 'Courier New', monospace;
  font-size: 0.65rem;
  font-weight: 600;
  color: #475569;
  letter-spacing: 0.3px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  white-space: nowrap;
}

.edc-card__badge-row {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 12px;
}

.edc-card__icon-row {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}

.edc-card__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--edc-color-text);
  margin: 0 0 6px 0;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.edc-card__header-full {
  font-size: 1.375rem;
  font-weight: 700;
  color: var(--edc-color-text);
  margin: 0 0 8px 0;
  line-height: 1.3;
  letter-spacing: -0.3px;
}

.edc-card__sub-header {
  font-size: 0.75rem;
  color: var(--edc-color-text-muted);
  margin: 0;
  line-height: 1.4;
}

.edc-card__sub-header-full {
  font-size: 0.9rem;
  color: var(--edc-color-text-muted);
  margin: 0;
  line-height: 1.5;
}

/* ===== BOTTOM WHITE SECTION ===== */
.edc-card__bottom {
  padding: 16px 24px 24px 24px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.edc-card__bottom--full {
  padding: 32px 32px;
}

.edc-card__body-preview {
  background: #f9fafb;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
  flex-grow: 1;
  overflow: hidden;
  min-height: 60px;
}

.edc-card__footer-text {
  font-size: 0.75rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.4;
  text-align: center;
}

.edc-card__footer {
  font-size: 0.8rem;
  color: #6b7280;
  line-height: 1.5;
  margin: 0 !important;
  padding: 0;
  text-align: center;
}

/* ===== SHARED ELEMENTS ===== */
.edc-icon {
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 2px solid;
}

.edc-icon--lg {
  width: 72px;
  height: 72px;
}

.edc-icon--lg .mdi {
  font-size: 2.5rem;
}

.edc-icon--sm {
  width: 48px;
  height: 48px;
}

.edc-icon--sm .mdi {
  font-size: 1.5rem;
}

.edc-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 50px;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.4px;
}

.edc-body-content {
  font-size: 0.8rem;
  color: #4b5563;
  line-height: 1.6;
  text-align: left;
}

.edc-body-content--full {
  margin: 0;
  font-size: 0.85rem;
}

.edc-body-content >>> p,
.edc-body-content >>> div,
.edc-body-content >>> span {
  margin-bottom: 0.5em;
}

.edc-body-content >>> h5,
.edc-body-content >>> h6 {
  font-weight: 600;
  color: var(--edc-color-text);
  margin-bottom: 0.75em;
  margin-top: 0;
  font-size: 0.9rem;
}

.edc-body-content >>> ul,
.edc-body-content >>> ol {
  padding-left: 1.5em;
  margin-bottom: 0.5em;
  margin-top: 0;
}

.edc-body-content >>> li {
  margin-bottom: 0.5em;
  line-height: 1.6;
}

.edc-body-content >>> strong {
  font-weight: 600;
  color: var(--edc-color-text);
}

.edc-no-content {
  color: var(--edc-color-text-muted);
  font-style: italic;
  font-size: 0.8rem;
}

/* ===== BUTTON STYLING ===== */
.edc-btn-action {
  padding: 10px 16px !important;
  border-radius: 6px !important;
  font-weight: 600;
  font-size: 0.9rem;
  color: #ffffff !important;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none !important;
  background-color: #0d6efd !important;
  background-image: none !important;
}

.edc-btn-action:hover:not(:disabled) {
  background-color: #0b5ed7 !important;
}

.edc-btn-action:disabled {
  opacity: 0.65;
  cursor: not-allowed;
  background-color: #0d6efd !important;
}

/* Action wrapper */
.edc-card__action-wrapper {
  margin-bottom: 0;
}

/* Actions row styling */
.edc-card__actions {
  gap: 8px;
}

.edc-card__action-wrapper {
  border-top: 1.5px solid #e5e7eb;
  padding-top: 16px;
  margin-top: 8px;
}

/* PENTING: DIPERBAIKI MENGGUNAKAN MARGIN AGAR BULLET IKUT GESER */
.edc-body-content >>> li.ql-indent-1 {
  margin-left: 1.5em;
  list-style-type: circle; /* Sub-bullet tingkat 1 (lingkaran putih) */
}

.edc-body-content >>> li.ql-indent-2 {
  margin-left: 3em;
  list-style-type: square; /* Sub-bullet tingkat 2 */
}

/* PENTING: DIPERBAIKI MENGGUNAKAN MARGIN AGAR BULLET IKUT GESER */
.edc-body-content >>> li.ql-indent-3 {
  margin-left: 4.5em;
  list-style-type: circle; /* Sub-bullet tingkat 1 (lingkaran putih) */
}

.edc-body-content >>> li.ql-indent-4 {
  margin-left: 6em;
  list-style-type: square; /* Sub-bullet tingkat 2 */
}

.edc-card__meta {
  font-size: 0.75rem;
  color: #6b7280;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.edc-meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.edc-meta-item .mdi {
  font-size: 1rem;
}
</style>
